/**
 * 会議ボード（タブの中身）取得フック。
 *
 * ボードの中身は deploy 供給なので、ここは「取ってくる形」だけを持ち、業務語彙は持たない。
 * workspaceId は各コンポーネントに配線させず、ここで解決する（呼び出し側の差分を小さく保つ）。
 *
 * サーバ契約: packages/core/src/api/web-ui/meeting-board.ts
 */
import { useQueries, useQuery } from '@tanstack/react-query'

import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import { getApiClient } from '@/lib/api'

type BoardSection = 'previous' | 'progress' | 'metrics' | 'daily'

const STALE_TIME_MS = 5 * 60 * 1000

/** 解決済み workspace id（未解決なら空文字。クエリは enabled=false で待つ）。 */
function useBoardWorkspaceId(): string {
  return useResolvedWorkspace().workspaceId
}

export function useBoardTabs<T>() {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'tabs', workspaceId],
    queryFn: () => getApiClient().getBoardTabs<T>(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: STALE_TIME_MS,
  })
}

export function useBoardSection<T>(section: BoardSection, tab: string) {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', section, workspaceId, tab],
    queryFn: () => getApiClient().getBoardSection<T>(workspaceId, section, tab),
    enabled: workspaceId.length > 0 && tab.length > 0,
    staleTime: STALE_TIME_MS,
  })
}

/** 複数タブの同じ断片をまとめて取る。取得経路をこのモジュールに集約するための入口。 */
export function useBoardSections<T>(section: BoardSection, tabs: readonly string[]) {
  const workspaceId = useBoardWorkspaceId()
  return useQueries({
    queries: tabs.map((tab) => ({
      queryKey: ['meeting-board', section, workspaceId, tab],
      queryFn: () => getApiClient().getBoardSection<T>(workspaceId, section, tab),
      enabled: workspaceId.length > 0,
      staleTime: STALE_TIME_MS,
    })),
  })
}

export function useBoardCohort<T>() {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'cohort', workspaceId],
    queryFn: () => getApiClient().getBoardCohort<T>(workspaceId),
    enabled: workspaceId.length > 0,
    staleTime: STALE_TIME_MS,
  })
}

export function useBoardDocuments<T>(tab: string) {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'documents', workspaceId, tab],
    queryFn: () => getApiClient().getBoardDocuments<T>(workspaceId, tab),
    enabled: workspaceId.length > 0 && tab.length > 0,
    staleTime: STALE_TIME_MS,
  })
}

/** 資料本文。開いたときだけ取りに行く（初期表示に本文を載せない）。 */
export function useBoardDocument(key: string | null) {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'document', workspaceId, key],
    queryFn: () => getApiClient().getBoardDocument(workspaceId, key!),
    enabled: workspaceId.length > 0 && key !== null,
    staleTime: STALE_TIME_MS,
  })
}

export function useBoardReportDates<T>(reportType: string) {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'report-dates', workspaceId, reportType],
    queryFn: () => getApiClient().getBoardReportDates<T>(workspaceId, reportType),
    enabled: workspaceId.length > 0 && reportType.length > 0,
    staleTime: STALE_TIME_MS,
  })
}

export function useBoardReport<T>(reportType: string, date: string | null) {
  const workspaceId = useBoardWorkspaceId()
  return useQuery({
    queryKey: ['meeting-board', 'report', workspaceId, reportType, date],
    queryFn: () => getApiClient().getBoardReport<T>(workspaceId, reportType, date ?? undefined),
    enabled: workspaceId.length > 0 && reportType.length > 0,
    staleTime: STALE_TIME_MS,
  })
}
